import React, { useState, useEffect, useCallback } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { assignmentBlock } from '@/lib/friendlyCodes';
import { ArrowRightLeft, CheckCircle, XCircle, Clock, User, Calendar, AlertTriangle } from 'lucide-react';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

const STATUS_COLORS = {
  REQUESTED: 'bg-blue-100 text-blue-800',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  COMPLETED: 'bg-green-100 text-green-800',
};

function ApproveDialog({ request, open, onClose, staff, onSuccess }) {
  const [action, setAction] = useState('APPROVE');
  const [notes, setNotes] = useState('');
  const [replacementStaffId, setReplacementStaffId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (request) {
      setReplacementStaffId(request.replacement_staff_id || '');
      setNotes('');
      setAction('APPROVE');
    }
  }, [request]);

  const handleSubmit = async () => {
    if (action === 'APPROVE' && !replacementStaffId && !request?.replacement_staff_id) {
      toast.error('Please select a replacement staff member');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/roster/swap-request/${request.id}/approve`, {
        action,
        notes: notes || null,
      });
      toast.success(action === 'APPROVE' ? 'Swap approved' : 'Swap rejected');
      onSuccess();
      onClose();
    } catch (e) {
      const blk = assignmentBlock(e);
      if (blk) toast.error(`Swap not approved — ${blk.title}`, { description: blk.reasons.join(' · ') || undefined });
      else toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to process swap');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-blue-500" />
            Review Swap Request
          </DialogTitle>
          <DialogDescription>
            {request?.requester_name} requested to swap their shift on {request?.shift?.date}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Shift Details */}
          <Card className="bg-muted/50">
            <CardContent className="p-3 space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>{request?.shift?.date} {request?.shift?.start_time}-{request?.shift?.end_time}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>Participant: {request?.shift?.participant_name || 'N/A'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Reason */}
          <div>
            <Label className="text-muted-foreground text-xs">Reason for swap</Label>
            <p className="text-sm mt-1">{request?.reason || 'No reason provided'}</p>
          </div>

          {/* Replacement Staff */}
          {!request?.replacement_staff_id && action === 'APPROVE' && (
            <div>
              <Label>Replacement Staff</Label>
              <SearchSelect
                options={staff.filter(s => s.id !== request?.requested_by).map(s => ({ value: s.id, label: s.full_name || s.email || '', keywords: s.email || '' }))}
                value={replacementStaffId}
                onChange={setReplacementStaffId}
                placeholder="Select replacement"
                searchPlaceholder={STAFF_SEARCH_HINT}
                emptyText="No staff match that search"
                testId="swap-replacement-select"
              />
            </div>
          )}

          {request?.replacement_staff_id && (
            <div>
              <Label className="text-muted-foreground text-xs">Proposed replacement</Label>
              <p className="text-sm mt-1 font-medium">{request?.replacement_name || 'Selected'}</p>
            </div>
          )}

          {/* Action */}
          <div>
            <Label>Decision</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="swap-action-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="APPROVE">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Approve
                  </span>
                </SelectItem>
                <SelectItem value="REJECT">
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" /> Reject
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={action === 'REJECT' ? 'Reason for rejection...' : 'Any notes...'}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant={action === 'REJECT' ? 'destructive' : 'default'}
            data-testid="swap-submit"
          >
            {loading ? 'Processing...' : action === 'APPROVE' ? 'Approve Swap' : 'Reject Swap'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ShiftSwapRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approveDialog, setApproveDialog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [requestsRes, staffRes] = await Promise.all([
        api.get(`/roster/swap-requests${params}`),
        fetchAllPages('/admin/users').catch(() => []),   // NEW-007: all users, not the first page of 20
      ]);
      setRequests(requestsRes.data.data || []);
      setStaff((staffRes || []).filter(s => s.role_name === 'Support Worker'));
    } catch (e) {
      toast.error('Failed to load swap requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pendingCount = requests.filter(r => ['REQUESTED', 'PENDING_APPROVAL'].includes(r.status)).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="swap-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ArrowRightLeft className="w-6 h-6 text-blue-500" />
            Shift Swap Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Review and manage staff shift swap requests
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {pendingCount} Pending
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="swap-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="REQUESTED">Requested</SelectItem>
            <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
            <SelectItem value="ACCEPTED">Accepted</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{requests.length} requests</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ArrowRightLeft className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No swap requests found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Staff can request shift swaps from their roster view
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shift Date</TableHead>
                  <TableHead>Shift Time</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Replacement</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map(r => (
                  <TableRow key={r.id} data-testid={`swap-row-${r.id}`}>
                    <TableCell className="font-mono text-sm">
                      {r.shift?.date || 'N/A'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {r.shift?.start_time}-{r.shift?.end_time}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{r.requester_name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.shift?.participant_name || 'No participant'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.replacement_name || (
                        <span className="text-muted-foreground italic text-sm">Not specified</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="text-sm truncate" title={r.reason}>
                        {r.reason || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[r.status] || 'bg-gray-100'}>
                        {r.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {['REQUESTED', 'PENDING_APPROVAL'].includes(r.status) && (
                        <Button
                          size="sm"
                          onClick={() => setApproveDialog(r)}
                          data-testid={`review-swap-${r.id}`}
                        >
                          Review
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {approveDialog && (
        <ApproveDialog
          request={approveDialog}
          open={!!approveDialog}
          onClose={() => setApproveDialog(null)}
          staff={staff}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
}
